import { useState, useEffect } from 'react'
import { reservationApi, doorApi, logApi } from '../services/api'
import { Reservation, AccessLog } from '../types'
import { useAuth } from '../hooks/useAuth'

export default function AdminDashboard() {
  const [reservations, setReservations] = useState<Reservation[]>([])
  const [logs, setLogs] = useState<AccessLog[]>([])
  const [doorStatus, setDoorStatus] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [doorLoading, setDoorLoading] = useState(false)
  const { logout } = useAuth()

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const today = new Date().toISOString().split('T')[0]
      const [resResult, logResult] = await Promise.all([
        reservationApi.getAll({ status: 'CONFIRMED', limit: 10 }),
        logApi.getLogs({ limit: 10 })
      ])
      setReservations(resResult.data.reservations)
      setLogs(logResult.data.logs)

      try {
        const doorResult = await doorApi.getStatus()
        setDoorStatus(doorResult.data)
      } catch {
        setDoorStatus(null)
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleLock = async () => {
    setDoorLoading(true)
    try {
      await doorApi.lock()
      alert('施錠しました')
      fetchData()
    } catch {
      alert('施錠に失敗しました')
    } finally {
      setDoorLoading(false)
    }
  }

  const handleUnlock = async () => {
    setDoorLoading(true)
    try {
      await doorApi.unlock()
      alert('解錠しました')
      fetchData()
    } catch {
      alert('解錠に失敗しました')
    } finally {
      setDoorLoading(false)
    }
  }

  const handleCancel = async (id: string) => {
    if (!window.confirm('この予約をキャンセルしますか？')) return
    try {
      await reservationApi.cancel(id)
      fetchData()
    } catch {
      alert('キャンセルに失敗しました')
    }
  }

  const handleExportCsv = async () => {
    try {
      const response = await logApi.exportCsv()
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'access_logs.csv')
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch {
      alert('エクスポートに失敗しました')
    }
  }

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })

  if (loading) return <div style={styles.loading}>読み込み中...</div>

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>管理者ダッシュボード</h1>
        <button onClick={logout} style={styles.logoutButton}>ログアウト</button>
      </div>

      {/* ドア操作 */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>ドア操作</h2>
        <div style={styles.doorStatus}>
          <span>現在の状態: </span>
          <strong>{doorStatus?.lockState || '取得中...'}</strong>
        </div>
        <div style={styles.doorButtons}>
          <button
            onClick={handleUnlock}
            style={styles.unlockButton}
            disabled={doorLoading}
          >
            解錠
          </button>
          <button
            onClick={handleLock}
            style={styles.lockButton}
            disabled={doorLoading}
          >
            施錠
          </button>
        </div>
      </div>

      {/* 本日の予約 */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>直近の予約</h2>
        {reservations.length === 0 ? (
          <p style={styles.empty}>予約はありません</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>利用者</th>
                <th style={styles.th}>開始</th>
                <th style={styles.th}>終了</th>
                <th style={styles.th}>ステータス</th>
                <th style={styles.th}>操作</th>
              </tr>
            </thead>
            <tbody>
              {reservations.map((r) => (
                <tr key={r.id}>
                  <td style={styles.td}>
                    {r.user?.name || r.guestName || 'ゲスト'}
                  </td>
                  <td style={styles.td}>{formatDate(r.startAt)}</td>
                  <td style={styles.td}>{formatDate(r.endAt)}</td>
                  <td style={styles.td}>{r.status}</td>
                  <td style={styles.td}>
                    <button
                      onClick={() => handleCancel(r.id)}
                      style={styles.cancelButton}
                    >
                      キャンセル
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 入退室ログ */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>直近の入退室ログ</h2>
          <button onClick={handleExportCsv} style={styles.exportButton}>
            CSVエクスポート
          </button>
        </div>
        {logs.length === 0 ? (
          <p style={styles.empty}>ログはありません</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>日時</th>
                <th style={styles.th}>ユーザー</th>
                <th style={styles.th}>イベント</th>
                <th style={styles.th}>トリガー</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td style={styles.td}>{formatDate(log.occurredAt)}</td>
                  <td style={styles.td}>
                    {log.user?.name || 'ゲスト'}
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      color: log.eventType === 'UNLOCK' ? 'green'
                        : log.eventType === 'LOCK' ? 'blue' : 'red'
                    }}>
                      {log.eventType}
                    </span>
                  </td>
                  <td style={styles.td}>{log.triggerType}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

const styles: { [key: string]: React.CSSProperties } = {
  container: { padding: '20px', maxWidth: '1200px', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  title: { color: '#1F4E79', margin: 0 },
  loading: { textAlign: 'center', padding: '40px' },
  section: { backgroundColor: 'white', borderRadius: '8px', padding: '20px', marginBottom: '24px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  sectionTitle: { color: '#333', marginTop: 0 },
  doorStatus: { marginBottom: '16px', fontSize: '16px' },
  doorButtons: { display: 'flex', gap: '12px' },
  unlockButton: { padding: '10px 24px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '16px' },
  lockButton: { padding: '10px 24px', backgroundColor: '#2ecc71', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '16px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { backgroundColor: '#1F4E79', color: 'white', padding: '10px', textAlign: 'left', fontSize: '14px' },
  td: { padding: '10px', borderBottom: '1px solid #eee', fontSize: '14px' },
  cancelButton: { padding: '4px 12px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' },
  exportButton: { padding: '8px 16px', backgroundColor: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' },
  logoutButton: { padding: '8px 16px', backgroundColor: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  empty: { color: '#888', textAlign: 'center', padding: '20px' }
}
